import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthUrl, getAccessToken, saveTokens, syncData } from '@/lib/googleFit';
import { supabase } from '@/lib/supabase';
import { Watch, CheckCircle2, RefreshCw, XCircle, Loader2 } from 'lucide-react';
import { SamsungHealthConnect } from './SamsungHealthConnect';

export function GoogleFitConnect() {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser) return;

    // Check if we have a code in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      handleAuthCode(code);
    }

    // Check existing connection
    const checkConnection = async () => {
      try {
        const { data, error } = await supabase
          .from('google_fit_tokens')
          .select('access_token')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        setConnected(!!data?.access_token);
      } catch (error) {
        console.error('Error checking Google Fit connection:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de vérifier la connexion Google Fit.",
        });
      }
    };

    checkConnection();
  }, [currentUser, toast]);

  const handleConnect = () => {
    try {
      setConnecting(true);
      const authUrl = getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating Google Fit connection:', error);
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: "Impossible d'initialiser la connexion à Google Fit.",
      });
      setConnecting(false);
    }
  };

  const handleAuthCode = async (code: string) => {
    try {
      setConnecting(true);
      const tokens = await getAccessToken(code);
      
      if (!currentUser) throw new Error('User not found');
      await saveTokens(currentUser.id, tokens);
      
      setConnected(true);
      toast({
        title: "Connexion réussie",
        description: "Votre montre est maintenant connectée.",
      });

      // Clear the code from the URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('Error connecting watch:', error);
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: "Impossible de connecter votre montre.",
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!currentUser) return;

    try {
      setConnecting(true);
      
      // Delete tokens from database
      const { error } = await supabase
        .from('google_fit_tokens')
        .delete()
        .eq('user_id', currentUser.id);

      if (error) throw error;
      
      setConnected(false);
      toast({
        title: "Déconnexion réussie",
        description: "Votre montre a été déconnectée.",
      });
    } catch (error) {
      console.error('Error disconnecting watch:', error);
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: "Impossible de déconnecter votre montre.",
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    if (!currentUser) return;

    try {
      setSyncing(true);
      await syncData(currentUser.id);
      
      toast({
        title: "Synchronisation réussie",
        description: "Les données ont été mises à jour.",
      });
    } catch (error) {
      console.error('Error syncing data:', error);
      toast({
        variant: "destructive",
        title: "Erreur de synchronisation",
        description: "Impossible de synchroniser les données.",
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Samsung Health Connection */}
      <SamsungHealthConnect />

      {/* Google Fit Connection */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Watch className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Google Fit</h2>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {connected ? (
              <>
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Montre connectée</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDisconnect}
                    disabled={connecting}
                    className="flex-1"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Déconnexion...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Déconnecter
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleSync}
                    disabled={syncing}
                    className="flex-1"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Synchronisation...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Synchroniser
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="w-full"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <Watch className="w-4 h-4 mr-2" />
                    Connecter ma montre
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}